// Shared helpers for the AI Maturity surface (SGNL-28/29/90).
//
// Single source of truth for the confidence → chip-class mapping and the
// suggestion-badge HTML. Was duplicated 3× before this lib (deals.ts /
// opp_detail.ts / maturity.ts inline ternaries) — flagged by
// code-reviewer agent on PR #40. Threshold changes now land in one file.

import { esc } from "./format";
import type { MaturitySuggestionBrief } from "../types";

// Map suggestion confidence to the existing chip palette (zero new CSS).
// Acceptance criterion from SGNL-29: "Badge color encodes confidence
// (green ≥0.8, yellow 0.6-0.8, gray <0.6)" — the existing palette maps
// aqua / indigo / blue onto those bands.
export function chipClassForConfidence(conf: number): string {
  if (conf >= 0.8) return "chip aqua";
  if (conf >= 0.6) return "chip indigo";
  return "chip blue";
}

/** Render a maturity suggestion as a "suggested: Stage N (XX%)" chip. */
export function renderMaturityBadge(
  s: MaturitySuggestionBrief | null | undefined,
  testid = "maturity-badge",
): string {
  if (!s) return "";
  // SGNL-107 + AR #2 / PR-Agent T1 on PR #54: coerce confidence ONCE
  // and pass the numeric to chipClassForConfidence + the formatters.
  // Pre-fix, `chipClassForConfidence(s.confidence)` could crash when
  // confidence is a runtime-tampered string (no >= operator on string
  // vs number in some JS contexts; or NaN comparisons silently mis-
  // bucket the chip). Coerce, gate everything on Number.isFinite, and
  // omit attributes entirely when invalid.
  const stageNum = Number(s.suggested_stage);
  const confNum = Number(s.confidence);
  const stageOk = Number.isFinite(stageNum);
  const confOk = Number.isFinite(confNum);
  const cls = confOk ? chipClassForConfidence(confNum) : "chip";
  const stageAttr = stageOk ? ` data-stage="${esc(String(stageNum))}"` : "";
  const confAttr = confOk ? ` data-conf="${esc(confNum.toFixed(2))}"` : "";
  // Text content uses the same coerced numeric (or "?" when invalid)
  // so the payload can never contain attribute- or tag-breaking chars.
  // No double-esc on stageText: the value is a String() of a finite
  // Number, which is digits + optional `.` only (no HTML-significant
  // characters). PR-Agent T1: drop the redundant outer esc().
  const stageText = stageOk ? String(stageNum) : "?";
  const conf = confOk ? (confNum * 100).toFixed(0) : "?";
  return (
    `<span class="${cls}" data-testid="${esc(testid)}"${stageAttr}${confAttr}>` +
    `suggested: Stage ${stageText} (${conf}%)</span>`
  );
}

// Extract the integer stage number from a rep-set ai_maturity string
// (e.g. "Stage 3: Transform" → 3). Returns null when the string
// doesn't fit the canonical Stage-N shape — legacy values like "Other"
// or stages outside 1-5 fall through to null so callers can decide
// whether to render the suggestion alongside the rep value.
export function repStageNumber(repValue: string | null | undefined): number | null {
  if (!repValue) return null;
  const m = repValue.match(/^Stage ([1-5])(?::|\s|$)/);
  return m ? parseInt(m[1], 10) : null;
}
